import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { emailContactsApi, type EmailContact, type EmailContactSource } from '../api';
import { Loader2, ArrowLeft } from 'lucide-react';

type HistoryData = {
  recipients: Array<{ id: number; campaignId: number; status: string; sentAt?: string | null; createdAt: string }>;
  suppressions: Array<{ id: number; reason: string; createdAt: string }>;
  sources: EmailContactSource[];
};

const SOURCE_LABEL: Record<string, string> = {
  USER_DATABASE: 'Usuario Sabuesos',
  FIREBASE_IMPORT: 'Firebase',
  CSV_IMPORT: 'Importación CSV',
  MANUAL: 'Manual',
  OTHER: 'Otro',
};

export default function EmailContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState<(EmailContact & { sources: EmailContactSource[] }) | null>(null);
  const [history, setHistory] = useState<HistoryData | null>(null);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const [contactData, historyData] = await Promise.all([
          emailContactsApi.findById(Number(id)),
          emailContactsApi.history(Number(id)),
        ]);
        setContact(contactData);
        setHistory(historyData);
      } catch (error: any) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error?.response?.data?.message || 'No se pudo cargar el contacto.',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6 max-w-4xl">
        <Link to="/admin/email-contacts">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver
          </Button>
        </Link>

        {loading ? (
          <Card className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
          </Card>
        ) : !contact ? (
          <Card className="p-8 text-center text-muted-foreground">Contacto no encontrado.</Card>
        ) : (
          <>
            <Card className="p-6 space-y-4">
              <div>
                <h1 className="text-2xl font-bold">{contact.email}</h1>
                {contact.name && <p className="text-muted-foreground">{contact.name}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                {contact.isSuppressed && <Badge variant="destructive">Suprimido</Badge>}
                {contact.isUnsubscribed && <Badge variant="secondary">Unsubscribed</Badge>}
                {!contact.isEmailValid && <Badge variant="outline">Inválido</Badge>}
                {contact.isEmailValid && !contact.isSuppressed && !contact.isUnsubscribed && (
                  <Badge variant="default">Activo</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Usuario Sabuesos</p>
                  <p>{contact.linkedUserId ? `#${contact.linkedUserId}` : 'No vinculado'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Fecha de inclusión</p>
                  <p>{new Date(contact.createdAt).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Último envío</p>
                  <p>{contact.lastEmailSentAt ? new Date(contact.lastEmailSentAt).toLocaleDateString() : 'Nunca'}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 space-y-3">
              <h2 className="text-lg font-semibold">Orígenes</h2>
              {history?.sources.length ? (
                <ul className="space-y-2">
                  {history.sources.map((s) => (
                    <li key={s.id} className="text-sm flex items-center justify-between border-b pb-2">
                      <span>{SOURCE_LABEL[s.sourceType] || s.sourceType}</span>
                      <span className="text-muted-foreground">{new Date(s.createdAt).toLocaleDateString()}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">Sin orígenes registrados.</p>
              )}
            </Card>

            <Card className="p-6 space-y-3">
              <h2 className="text-lg font-semibold">Campañas</h2>
              {history?.recipients.length ? (
                <ul className="space-y-2">
                  {history.recipients.map((r) => (
                    <li key={r.id} className="text-sm flex items-center justify-between border-b pb-2">
                      <span>Campaña #{r.campaignId}</span>
                      <Badge variant="outline">{r.status}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">Este contacto no ha recibido campañas.</p>
              )}
            </Card>

            <Card className="p-6 space-y-3">
              <h2 className="text-lg font-semibold">Suppression</h2>
              {history?.suppressions.length ? (
                <ul className="space-y-2">
                  {history.suppressions.map((s) => (
                    <li key={s.id} className="text-sm flex items-center justify-between border-b pb-2">
                      <span>{s.reason}</span>
                      <span className="text-muted-foreground">{new Date(s.createdAt).toLocaleDateString()}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">Sin registros de suppression.</p>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
